import dynamic from "@redux-dynostore/react-redux";
import subspaced from "@redux-dynostore/react-redux-subspace";
import { attachReducer } from "@redux-dynostore/redux-subspace";
import React, { useCallback, useEffect, useState } from "react";
import { connect } from "react-redux";
import { sendNotification } from "../../ToastManager/ToastManager";
import { ToastType } from "../../ToastManager/constants";
import styled, { withTheme } from "styled-components";

import PieChart from "../../ui-library/PieChart/PieChart";
import HorizontalProgressBarChart from "../../ui-library/HorizontalProgressBarChart/HorizontalProgressBarChart";

import {
  dietDiaryChartsReducer,
  mapDispatchToProps,
  mapStateToProps,
} from "./DietDiaryCharts.store";
import moment from "moment";
import {
  dailyTargetIds,
  getBaseDateFormat,
  kcalMultiplier,
} from "../../../modules/constants";
import { useTranslation } from "react-i18next";
import {
  roundMealPercentage,
  roundMealValue,
} from "../../../helpers/mealsHelper";
import NutritionItem from "../../library/NutritionItem/NutritionItem";
import { DEFAULT_MOMENT_LOCALE, isRtl } from "../../../i18n/helpers";

const StyledPieChart = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
`;

const StyledHPBC = styled.div`
  margin-top: 20px;
`;

export const SmartDietDiaryCharts = (props) => {
  const {
    startDate,
    getNutrientById,
    fetchMealsByDate,
    getMacronutrientColor,
    hpbcImpact,
    hpbcVals,
    setHpbcVals,
    existingDayMeals,
    calories,
    pieChartSeries,
    participantId,
    mealId,
    theme,
    rerenderPieChart,
  } = props;

  const { t } = useTranslation();

  const [pieChartKey, setPieChartKey] = useState("pie-chart");

  useEffect(() => {
    if (rerenderPieChart) {
      setPieChartKey(moment().toISOString());
    }
  }, [rerenderPieChart]);

  const getMealImpactTitle = useCallback(() => {
    const date = moment(startDate).startOf("day");
    const today = moment(new Date()).startOf("day");

    if (date.isSame(today)) {
      return t("appText.dietDiary.mealImpact.today.title");
    } else if (date.isBefore(today)) {
      const yesterday = today.subtract(1, "day").startOf("day");

      if (date.isSame(yesterday)) {
        return t("appText.dietDiary.mealImpact.yesterday.title");
      }
    }

    return t("appText.dietDiary.mealImpact.date.title", {
      date: date.format(getBaseDateFormat()),
    });
  }, [startDate, t]);

  const getAboutMealImpactValue = useCallback(
    (searchObj, searchAttrId) => {
      const element = getNutrientById(searchAttrId);

      if (searchObj) {
        return searchObj[element?.aboutMealLetter]?.value ?? 0;
      }
      return 0;
    },
    [getNutrientById]
  );

  useEffect(() => {
    const individualTotals = {
      longChainFats: 0,
      mediumChainFats: 0,
      protein: 0,
      carbs: 0,
      calories: 0,
    };

    existingDayMeals?.forEach((item) => {
      if (item.mealId !== mealId) {
        individualTotals.longChainFats +=
          getAboutMealImpactValue(
            item.aboutMeal,
            dailyTargetIds.LONG_CHAIN_FATS_ATTR_ID
          ) * kcalMultiplier.lcf;
        individualTotals.mediumChainFats +=
          getAboutMealImpactValue(
            item.aboutMeal,
            dailyTargetIds.MEDIUM_CHAIN_FATS_ATTR_ID
          ) * kcalMultiplier.mcf;
        individualTotals.protein +=
          getAboutMealImpactValue(
            item.aboutMeal,
            dailyTargetIds.PROTEIN_ATTR_ID
          ) * kcalMultiplier.protein;
        individualTotals.carbs +=
          getAboutMealImpactValue(
            item.aboutMeal,
            dailyTargetIds.CARBS_ATTR_ID
          ) * kcalMultiplier.carbs;
        individualTotals.calories += getAboutMealImpactValue(
          item.aboutMeal,
          dailyTargetIds.CALORIES_ATTR_ID
        );
      }
    });

    const impact = {
      longChainFats: {},
      mediumChainFats: {},
      protein: {},
      carbs: {},
    };

    const currentLongChainFats =
      getAboutMealImpactValue(
        hpbcImpact,
        dailyTargetIds.LONG_CHAIN_FATS_ATTR_ID
      ) * kcalMultiplier.lcf;
    const currentMediumChainFats =
      getAboutMealImpactValue(
        hpbcImpact,
        dailyTargetIds.MEDIUM_CHAIN_FATS_ATTR_ID
      ) * kcalMultiplier.mcf;
    const currentProteinVal =
      getAboutMealImpactValue(hpbcImpact, dailyTargetIds.PROTEIN_ATTR_ID) *
      kcalMultiplier.protein;
    const currentCarbs =
      getAboutMealImpactValue(hpbcImpact, dailyTargetIds.CARBS_ATTR_ID) *
      kcalMultiplier.carbs;

    individualTotals.longChainFats += currentLongChainFats;
    individualTotals.mediumChainFats += currentMediumChainFats;
    individualTotals.protein += currentProteinVal;
    individualTotals.carbs += currentCarbs;

    const total = individualTotals.calories + calories.numericValue;

    impact.longChainFats = {
      value:
        total > 0
          ? roundMealPercentage(
              roundMealValue((currentLongChainFats / total) * 100.0)
            )
          : 0,
      sign: "+",
    };
    impact.mediumChainFats = {
      value:
        total > 0
          ? roundMealPercentage(
              roundMealValue((currentMediumChainFats / total) * 100.0)
            )
          : 0,
      sign: "+",
    };
    impact.protein = {
      value:
        total > 0
          ? roundMealPercentage(
              roundMealValue((currentProteinVal / total) * 100.0)
            )
          : 0,
      sign: "+",
    };
    impact.carbs = {
      value:
        total > 0
          ? roundMealPercentage(roundMealValue((currentCarbs / total) * 100.0))
          : 0,
      sign: "+",
    };

    const tmpChartData = [
      {
        label: isRtl()
          ? t("appText.dietDiary.longChainFats")
          : t("appText.dietDiary.lcf"),
        value:
          total > 0
            ? roundMealPercentage(
                roundMealValue((individualTotals.longChainFats / total) * 100.0)
              )
            : 0,
        valueGrams: roundMealValue(
          individualTotals.longChainFats / kcalMultiplier.lcf
        ),
        impact: impact.longChainFats,
        color: getMacronutrientColor(
          dailyTargetIds.LONG_CHAIN_FATS_ATTR_ID,
          theme
        ),
        unit: "%",
      },
      {
        label: isRtl()
          ? t("appText.dietDiary.mediumChainFats")
          : t("appText.dietDiary.mcf"),
        value:
          total > 0
            ? roundMealPercentage(
                roundMealValue(
                  (individualTotals.mediumChainFats / total) * 100.0
                )
              )
            : 0,
        valueGrams: roundMealValue(
          individualTotals.mediumChainFats / kcalMultiplier.mcf
        ),
        impact: impact.mediumChainFats,
        color: getMacronutrientColor(
          dailyTargetIds.MEDIUM_CHAIN_FATS_ATTR_ID,
          theme
        ),
        unit: "%",
      },
      {
        label: t("appText.dietDiary.protein"),
        value:
          total > 0
            ? roundMealPercentage(
                roundMealValue((individualTotals.protein / total) * 100.0)
              )
            : 0,
        valueGrams: roundMealValue(
          individualTotals.protein / kcalMultiplier.protein
        ),
        impact: impact.protein,
        color: getMacronutrientColor(dailyTargetIds.PROTEIN_ATTR_ID, theme),
        unit: "%",
      },
      {
        label: t("appText.dietDiary.carbohydrates"),
        value:
          total > 0
            ? roundMealPercentage(
                roundMealValue((individualTotals.carbs / total) * 100.0)
              )
            : 0,
        valueGrams: roundMealValue(
          individualTotals.carbs / kcalMultiplier.carbs
        ),
        impact: impact.carbs,
        color: getMacronutrientColor(dailyTargetIds.CARBS_ATTR_ID, theme),
        unit: "%",
      },
    ];

    setHpbcVals(tmpChartData);
  }, [
    existingDayMeals,
    getAboutMealImpactValue,
    setHpbcVals,
    hpbcImpact,
    getMacronutrientColor,
    mealId,
    theme,
    t,
    calories.numericValue,
  ]);

  useEffect(() => {
    if (startDate) {
      fetchMealsByDate(
        moment(startDate)
          .locale(DEFAULT_MOMENT_LOCALE)
          .format("YYYY/MM/DDT00:00:00"),
        participantId
      ).catch((error) => {
        sendNotification(error, ToastType.ERROR);
      });
    }
  }, [startDate, fetchMealsByDate, participantId]);

  return (
    <>
      <NutritionItem
        value={calories.value}
        title={t("appText.dietDiary.totalCalories")}
        backgroundColor={props.theme?.lightShadeMagenta}
        titleColor={props.theme?.darkPurple}
      />
      {calories.numericValue > 0 && (
        <StyledPieChart>
          <PieChart
            customKey={pieChartKey || "pie-chart"}
            series={pieChartSeries}
            width={380}
            height={300}
          />
        </StyledPieChart>
      )}
      {hpbcImpact && (
        <StyledHPBC>
          <HorizontalProgressBarChart
            title={getMealImpactTitle()}
            data={hpbcVals}
          />
        </StyledHPBC>
      )}
    </>
  );
};

export default dynamic(
  "dietDiaryChartsReducer",
  subspaced({
    // We map extra state because we might need to acess other stores for data, subspace is self contained.
    mapExtraState: (state) => ({ loginReducer: state.loginReducer }),
  }),
  attachReducer(dietDiaryChartsReducer)
)(
  connect(mapStateToProps, mapDispatchToProps)(withTheme(SmartDietDiaryCharts))
);
